import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Trash2, X } from '../../icons/wp-icons';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { deleteOrganisation } from '../../api/stripeApi';

function DangerZoneSettings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);

  // Nur für Organisation Owner anzeigen
  if (!user?.isOrganisationOwner) {
    return null;
  }

  const organisationName = user?.organisation?.name || '';

  const handleDeleteOrganisation = async () => {
    if (deleteConfirmation !== organisationName) {
      toast.error('Organisationsname stimmt nicht überein');
      return;
    }

    setIsDeleting(true);
    try {
      await deleteOrganisation(deleteConfirmation);
      toast.success('Organisation wurde gelöscht');
      // Logout und Redirect
      logout();
      navigate('/');
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.error || 'Löschen fehlgeschlagen');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <div className="bg-surface rounded-card shadow-card overflow-hidden border-2 border-red-200">
        {/* Header */}
        <div className="p-4 sm:p-6 bg-red-50 border-b border-red-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg shrink-0">
              <AlertTriangle className="w-5 h-5 sm:w-6 sm:h-6 text-red-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-lg sm:text-xl font-semibold text-red-800">Gefahrenzone</h2>
              <p className="text-xs sm:text-sm text-red-600 hidden sm:block">
                Diese Aktionen können nicht rückgängig gemacht werden
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 p-4 border border-red-200 rounded-lg bg-red-50/50">
            <div>
              <h3 className="font-medium text-gray-900">Organisation löschen</h3>
              <p className="text-sm text-gray-600 mt-1">
                Löscht alle Daten unwiderruflich: Kunden, Rechnungen, Termine, Mitarbeiter
              </p>
            </div>
            <button
              onClick={() => setShowDeleteModal(true)}
              className="px-4 py-2 bg-danger text-white rounded-button hover:bg-red-700 transition flex items-center gap-2 shrink-0"
            >
              <Trash2 className="w-4 h-4" />
              Löschen
            </button>
          </div>
        </div>
      </div>

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-red-600">Organisation löschen</h3>
                </div>
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmation('');
                  }}
                  className="p-1 hover:bg-gray-100 rounded-lg transition"
                >
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="p-4 bg-red-50 rounded-lg mb-4">
                <p className="text-sm text-red-800 font-medium">
                  Dies wird unwiderruflich löschen:
                </p>
                <ul className="mt-2 text-sm text-red-700 list-disc list-inside space-y-1">
                  <li>Alle Kunden und Kontakte</li>
                  <li>Alle Rechnungen und Angebote</li>
                  <li>Alle Termine und Projekte</li>
                  <li>Alle Mitarbeiter-Zugänge</li>
                  <li>Das aktive Abonnement</li>
                  <li>Alle E-Mail-Konversationen</li>
                </ul>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Geben Sie <strong className="text-red-600">{organisationName}</strong> ein um zu bestätigen:
                </label>
                <input
                  type="text"
                  value={deleteConfirmation}
                  onChange={(e) => setDeleteConfirmation(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                  placeholder="Organisationsname eingeben"
                  autoComplete="off"
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmation('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-medium"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleDeleteOrganisation}
                  disabled={deleteConfirmation !== organisationName || isDeleting}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isDeleting ? (
                    <span className="flex items-center justify-center gap-2">
                      <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></span>
                      Wird gelöscht...
                    </span>
                  ) : (
                    'Endgültig löschen'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default DangerZoneSettings;
